import {
  chartHasValues,
  chartHeight,
  chartLabelWithUnit,
  chartSwatch,
  chartValue,
  chartX,
  horizontalBars,
} from "@agent/chartData";
import { chartNodes } from "@agent/chartExports";
import type { UiChartNode } from "@agent/uiSchema";
import { useElementWidth } from "@utils/use-element-width";
import { lazy, Suspense, useId, useLayoutEffect, useRef } from "react";
import { ErrorBoundary } from "react-error-boundary";

const ChartGraphic = lazy(() =>
  import("@agent/ChartGraphic").then((module) => ({
    default: module.ChartGraphic,
  })),
);

export const UiChart = ({ node }: { node: UiChartNode }) => {
  const titleId = useId();
  const figureRef = useRef<HTMLElement>(null);
  const [plotRef, width] = useElementWidth();
  const hasValues = chartHasValues(node);
  const yLabel =
    node.yLabel ?? (node.series.length === 1 ? node.series[0].label : "Value");
  const numericLabel = chartLabelWithUnit(yLabel, node.unit);
  const legend =
    node.chartType === "pie"
      ? node.data.map((row) => ({
          label: String(row[node.xKey]),
          value: chartValue(node, row[node.series[0].key]),
        }))
      : node.series;

  useLayoutEffect(() => {
    const figure = figureRef.current;
    if (!figure) return;
    chartNodes.set(figure, node);
    return () => {
      chartNodes.delete(figure);
    };
  }, [node]);

  return (
    <figure
      ref={figureRef}
      data-ui-chart
      aria-labelledby={titleId}
      className="min-w-0 w-full space-y-3 text-ink-body"
    >
      <figcaption>
        <h3
          id={titleId}
          className="break-words font-display text-lg font-semibold text-ink"
        >
          {node.title}
        </h3>
        {node.description && (
          <p className="mt-1 whitespace-pre-wrap break-words text-sm text-annotation">
            {node.description}
          </p>
        )}
      </figcaption>
      {node.chartType !== "pie" && (
        <p className="break-words text-xs text-annotation">
          {horizontalBars(node) ? (node.xLabel ?? node.xKey) : numericLabel}
        </p>
      )}
      <div
        ref={plotRef}
        data-chart-plot
        className="min-w-0 w-full"
        style={{ height: chartHeight(node) }}
      >
        {!hasValues ? (
          <div className="flex h-full items-center justify-center rounded-paper border border-dashed border-rule px-4 text-center text-sm text-annotation">
            {node.chartType === "pie" && node.data.length
              ? "No positive values to display."
              : "No measurements to display yet."}
          </div>
        ) : (
          <ErrorBoundary
            resetKeys={[node]}
            fallback={
              <p className="p-4 text-sm text-annotation">
                The chart could not load. Its measurements are available in the
                data table below.
              </p>
            }
          >
            <Suspense
              fallback={
                <p role="status" className="p-4 text-sm text-annotation">
                  Loading chart…
                </p>
              }
            >
              {width !== null && width > 0 && (
                <ChartGraphic node={node} width={width} />
              )}
            </Suspense>
          </ErrorBoundary>
        )}
      </div>
      {node.chartType !== "pie" && (node.xLabel || horizontalBars(node)) && (
        <p className="break-words text-center text-xs text-annotation">
          {horizontalBars(node) ? numericLabel : node.xLabel}
        </p>
      )}
      <ul
        aria-label="Chart legend"
        className="flex flex-wrap gap-x-5 gap-y-2 text-sm"
      >
        {legend.map((item, index) => (
          <li key={index} className="flex min-w-0 items-start gap-2">
            <span
              aria-hidden
              className="mt-1.5 h-2.5 w-2.5 shrink-0 rounded-sm"
              style={{ background: chartSwatch(node, index) }}
            />
            <span className="min-w-0 [overflow-wrap:anywhere]">
              {item.label}
              {"value" in item && (
                <span className="text-annotation"> · {item.value}</span>
              )}
            </span>
          </li>
        ))}
      </ul>
      <details className="text-sm" data-html2canvas-ignore>
        <summary className="w-fit cursor-pointer rounded-paper text-annotation underline-offset-4 hover:underline focus-visible:outline-2 focus-visible:outline-accent">
          View data ({node.data.length} rows)
        </summary>
        <div
          className="mt-3 max-h-80 overflow-auto rounded-paper border border-rule"
          tabIndex={0}
          role="region"
          aria-label={`${node.title} data`}
        >
          <table className="w-full border-collapse text-left text-sm">
            <caption className="sr-only">
              {node.title}
              {node.unit ? `, values in ${node.unit}` : ""}
            </caption>
            <thead className="sticky top-0 bg-recess text-ink">
              <tr>
                <th scope="col" className="px-3 py-2">
                  {node.xLabel ?? node.xKey}
                </th>
                {node.series.map((series) => (
                  <th scope="col" className="px-3 py-2" key={series.key}>
                    {chartLabelWithUnit(series.label, node.unit)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {node.data.map((row, index) => (
                <tr key={index} className="border-t border-rule">
                  <th scope="row" className="px-3 py-2 font-normal">
                    {chartX(node, row[node.xKey] as string | number)}
                  </th>
                  {node.series.map((series) => (
                    <td key={series.key} className="px-3 py-2 tabular-nums">
                      {chartValue(node, row[series.key])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          {!node.data.length && (
            <p className="p-3 text-annotation">No rows available.</p>
          )}
        </div>
      </details>
    </figure>
  );
};
